import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Chip,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SendIcon from "@mui/icons-material/Send";
import WebhookIcon from "@mui/icons-material/Webhook";
import useSimApi from "src/hooks/useSimApi";

const SIMULATOR_TYPE = process.env.REACT_APP_SIMULATOR_TYPE || "target";

interface AuthResponse {
  access_token: string;
  expires_in: number;
  token_type: string;
}

interface TriggerResponse {
  status_code: number;
  body: unknown;
  delivery_id?: string;
  timestamp?: string;
}

// Persist non-sensitive inputs across reloads so iterating on a trigger
// (tweak body → re-run) doesn't require re-typing flow_id + connector_id +
// the whole JSON payload every time. webhook_secret is intentionally NOT
// persisted — treat it like a password.
const LS_PREFIX = "sim_call_engine_";
const lsGet = (key: string, fallback: string): string => {
  try {
    const v = localStorage.getItem(LS_PREFIX + key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
};
const lsSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(LS_PREFIX + key, value);
  } catch {
    // ignore quota / private-mode failures — caching is best-effort.
  }
};

const CallEngine: React.FC = () => {
  const { request } = useSimApi();

  // Authenticate
  const [authBusy, setAuthBusy] = useState(false);
  const [authResult, setAuthResult] = useState<AuthResponse | null>(null);
  const [authError, setAuthError] = useState<{ message: string; detail?: string } | null>(null);

  // Synchronous trigger
  const [syncFlowId, setSyncFlowId] = useState(() => lsGet("sync_flow_id", ""));
  const [syncConnectorId, setSyncConnectorId] = useState(() =>
    lsGet("sync_m2m_connector_id", ""),
  );
  const [syncBody, setSyncBody] = useState(() =>
    lsGet("sync_body", '{\n  "your_field": "your_value"\n}'),
  );
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncResult, setSyncResult] = useState<TriggerResponse | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Webhook trigger
  const [hookFlowId, setHookFlowId] = useState(() => lsGet("hook_flow_id", ""));
  const [hookConnectionId, setHookConnectionId] = useState(() =>
    lsGet("hook_connection_id", ""),
  );
  const [hookSecret, setHookSecret] = useState("");
  const [hookBody, setHookBody] = useState(() =>
    lsGet("hook_body", '{\n  "your_event": "payload"\n}'),
  );
  const [hookBusy, setHookBusy] = useState(false);
  const [hookResult, setHookResult] = useState<TriggerResponse | null>(null);
  const [hookError, setHookError] = useState<string | null>(null);

  useEffect(() => lsSet("sync_flow_id", syncFlowId), [syncFlowId]);
  useEffect(() => lsSet("sync_m2m_connector_id", syncConnectorId), [syncConnectorId]);
  useEffect(() => lsSet("sync_body", syncBody), [syncBody]);
  useEffect(() => lsSet("hook_flow_id", hookFlowId), [hookFlowId]);
  useEffect(() => lsSet("hook_connection_id", hookConnectionId), [hookConnectionId]);
  useEffect(() => lsSet("hook_body", hookBody), [hookBody]);

  const parseJson = (text: string): { value: unknown; error: string | null } => {
    if (!text.trim()) return { value: {}, error: null };
    try {
      return { value: JSON.parse(text), error: null };
    } catch (e: any) {
      return { value: null, error: `Invalid JSON: ${e?.message ?? "parse error"}` };
    }
  };

  const handleAuthenticate = async () => {
    setAuthBusy(true);
    setAuthError(null);
    setAuthResult(null);
    try {
      const res = await request("/api/v1/engine/authenticate", "POST");
      const data = await res.json();
      if (res.ok) {
        setAuthResult(data as AuthResponse);
      } else {
        // The proxy returns either {error: "<short>"} for simple cases or
        // {error: "auth0_error", status_code, body} when Auth0 itself
        // rejects the request. Surface both so the user can see e.g.
        // "invalid_client" instead of an opaque label.
        const message = data?.error ?? `HTTP ${res.status}`;
        let detail: string | undefined;
        if (data?.status_code !== undefined || data?.body !== undefined) {
          const body =
            typeof data.body === "string"
              ? data.body
              : JSON.stringify(data.body, null, 2);
          detail = `Auth0 returned HTTP ${data.status_code ?? "?"}\n${body ?? ""}`;
        }
        setAuthError({ message, detail });
      }
    } catch (e: any) {
      setAuthError({ message: e?.message ?? "Network error" });
    } finally {
      setAuthBusy(false);
    }
  };

  const handleSyncTrigger = async () => {
    const { value, error: jsonErr } = parseJson(syncBody);
    if (jsonErr) {
      setSyncError(jsonErr);
      return;
    }
    if (!syncFlowId.trim() || !syncConnectorId.trim()) {
      setSyncError("flow_id and m2m_connector_id are required");
      return;
    }
    setSyncBusy(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const res = await request("/api/v1/engine/trigger-sync", "POST", {
        flow_id: syncFlowId.trim(),
        m2m_connector_id: syncConnectorId.trim(),
        payload: value,
      });
      const data = await res.json();
      if (res.ok) {
        setSyncResult(data as TriggerResponse);
      } else {
        setSyncError(data?.error ?? `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setSyncError(e?.message ?? "Network error");
    } finally {
      setSyncBusy(false);
    }
  };

  const handleWebhookTrigger = async () => {
    const { value, error: jsonErr } = parseJson(hookBody);
    if (jsonErr) {
      setHookError(jsonErr);
      return;
    }
    if (!hookFlowId.trim() || !hookConnectionId.trim() || !hookSecret.trim()) {
      setHookError("flow_id, connection_id and webhook_secret are required");
      return;
    }
    setHookBusy(true);
    setHookError(null);
    setHookResult(null);
    try {
      const res = await request("/api/v1/engine/trigger-webhook", "POST", {
        flow_id: hookFlowId.trim(),
        connection_id: hookConnectionId.trim(),
        webhook_secret: hookSecret,
        payload: value,
      });
      const data = await res.json();
      if (res.ok) {
        setHookResult(data as TriggerResponse);
      } else {
        setHookError(data?.error ?? `HTTP ${res.status}`);
      }
    } catch (e: any) {
      setHookError(e?.message ?? "Network error");
    } finally {
      setHookBusy(false);
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 900 }}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Typography variant="h5" fontWeight={600}>
          Call Engine
        </Typography>
        <Chip
          label={SIMULATOR_TYPE.toUpperCase()}
          size="small"
          color={SIMULATOR_TYPE === "target" ? "primary" : "secondary"}
          sx={{ fontWeight: 600 }}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Test the Strawbay engine end-to-end using the credentials configured
        under Settings → Strawbay Engine Authentication.
      </Typography>

      {/* Authenticate */}
      <Section title="Authenticate" icon={<LockOpenIcon sx={{ fontSize: 18 }} />}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Exchange the stored client_id / client_secret for an Auth0 access
          token. Tokens are cached server-side until 5 minutes before expiry.
        </Typography>
        <Button
          variant="contained"
          onClick={handleAuthenticate}
          disabled={authBusy}
          startIcon={authBusy ? <CircularProgress size={14} color="inherit" /> : <LockOpenIcon />}
          sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
        >
          {authBusy ? "Authenticating…" : "Get access token"}
        </Button>
        {authError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {authError.message}
            </Typography>
            {authError.detail && (
              <Box
                sx={{
                  mt: 1,
                  fontFamily: "monospace",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {authError.detail}
              </Box>
            )}
          </Alert>
        )}
        {authResult && (
          <Box sx={{ mt: 2, p: 2, bgcolor: "#f8f8ff", border: "1px solid #e0e0e0", borderRadius: 1 }}>
            <Field label="access_token" value={authResult.access_token} monospace />
            <Field label="expires_in" value={`${authResult.expires_in}s`} />
            <Field label="token_type" value={authResult.token_type} />
          </Box>
        )}
      </Section>

      {/* Synchronous trigger */}
      <Section title="Trigger via synchronous API" icon={<SendIcon sx={{ fontSize: 18 }} />}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          PUT <code>/flow/&lt;flow_id&gt;/synchronous</code>. The bearer token
          is obtained automatically using the stored credentials.
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="flow_id"
              size="small"
              value={syncFlowId}
              onChange={(e) => setSyncFlowId(e.target.value)}
              placeholder="5448351845711872"
              sx={{ flex: 1, "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
            />
            <TextField
              label="X-M2M-CONNECTOR-ID"
              size="small"
              value={syncConnectorId}
              onChange={(e) => setSyncConnectorId(e.target.value)}
              placeholder="5710735065743360"
              sx={{ flex: 1, "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
            />
          </Stack>
          <TextField
            label="Request body (JSON)"
            size="small"
            multiline
            minRows={5}
            value={syncBody}
            onChange={(e) => setSyncBody(e.target.value)}
            sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 12 } }}
          />
          <Box>
            <Button
              variant="contained"
              onClick={handleSyncTrigger}
              disabled={syncBusy}
              startIcon={syncBusy ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
              sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
            >
              {syncBusy ? "Triggering…" : "Trigger flow"}
            </Button>
          </Box>
        </Stack>
        {syncError && <Alert severity="error" sx={{ mt: 2 }}>{syncError}</Alert>}
        {syncResult && <ResponseBlock result={syncResult} />}
      </Section>

      {/* Webhook trigger */}
      <Section title="Trigger via webhook" icon={<WebhookIcon sx={{ fontSize: 18 }} />}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          POST <code>/webhook/&lt;flow_id&gt;</code> with an HMAC-SHA256
          signature computed over <code>&lt;timestamp&gt;.&lt;raw_body&gt;</code>.
          The webhook_secret comes from <code>Connection.webhook_secret</code>
          on the Console.
        </Typography>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="flow_id"
              size="small"
              value={hookFlowId}
              onChange={(e) => setHookFlowId(e.target.value)}
              placeholder="5448351845711872"
              sx={{ flex: 1, "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
            />
            <TextField
              label="X-CONNECTION-ID"
              size="small"
              value={hookConnectionId}
              onChange={(e) => setHookConnectionId(e.target.value)}
              placeholder="connection_id"
              sx={{ flex: 1, "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
            />
          </Stack>
          <TextField
            label="webhook_secret"
            size="small"
            type="password"
            value={hookSecret}
            onChange={(e) => setHookSecret(e.target.value)}
            placeholder="from Connection.webhook_secret in Console"
            sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 13 } }}
          />
          <TextField
            label="Webhook body (JSON)"
            size="small"
            multiline
            minRows={5}
            value={hookBody}
            onChange={(e) => setHookBody(e.target.value)}
            sx={{ "& .MuiOutlinedInput-input": { fontFamily: "monospace", fontSize: 12 } }}
          />
          <Box>
            <Button
              variant="contained"
              onClick={handleWebhookTrigger}
              disabled={hookBusy}
              startIcon={hookBusy ? <CircularProgress size={14} color="inherit" /> : <WebhookIcon />}
              sx={{ bgcolor: "#7166cf", "&:hover": { bgcolor: "#5a4dbd" }, textTransform: "none" }}
            >
              {hookBusy ? "Sending…" : "Send webhook"}
            </Button>
          </Box>
        </Stack>
        {hookError && <Alert severity="error" sx={{ mt: 2 }}>{hookError}</Alert>}
        {hookResult && <ResponseBlock result={hookResult} />}
      </Section>
    </Box>
  );
};

const Section: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({
  title,
  icon,
  children,
}) => (
  <Box sx={{ mb: 3, bgcolor: "#fff", border: "1px solid #e5e7eb", borderRadius: 1 }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
      <Box sx={{ color: "#7166cf", display: "flex" }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    </Stack>
    <Divider />
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const Field: React.FC<{ label: string; value: string; monospace?: boolean }> = ({
  label,
  value,
  monospace,
}) => (
  <Box sx={{ display: "flex", py: "6px", borderBottom: "1px solid #f3f4f6", "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ minWidth: 120, fontSize: 12, fontWeight: 600, color: "#6b7280" }}>
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: 12,
        flex: 1,
        wordBreak: "break-all",
        fontFamily: monospace ? "monospace" : undefined,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const ResponseBlock: React.FC<{ result: TriggerResponse }> = ({ result }) => {
  const ok = result.status_code >= 200 && result.status_code < 300;
  const bodyText =
    typeof result.body === "string"
      ? result.body
      : JSON.stringify(result.body, null, 2);
  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Chip
          label={`HTTP ${result.status_code}`}
          size="small"
          color={ok ? "success" : "error"}
          sx={{ fontWeight: 600 }}
        />
        {result.delivery_id && (
          <Typography variant="caption" sx={{ color: "#6b7280", fontFamily: "monospace" }}>
            X-Webhook-Id: {result.delivery_id}
          </Typography>
        )}
      </Stack>
      <Box
        sx={{
          p: 1.5,
          bgcolor: ok ? "#f8f8ff" : "#fff5f5",
          border: `1px solid ${ok ? "#e0e0e0" : "#fecaca"}`,
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: 12,
          whiteSpace: "pre-wrap",
          wordBreak: "break-all",
          maxHeight: 280,
          overflow: "auto",
        }}
      >
        {bodyText}
      </Box>
    </Box>
  );
};

export default CallEngine;
